import Ajv, { ErrorObject, ValidateFunction } from 'ajv';
import type { DiagramConfig } from '../types/interfaces';

// Import all schemas
import diagramSchema from '../schemas/diagram.schema.json';
import tenframeSchema from '../schemas/components/tenframe.schema.json';
import sliderSchema from '../schemas/components/slider.schema.json';
import gridlayoutSchema from '../schemas/components/gridlayout.schema.json';
import textSchema from '../schemas/components/text.schema.json';
import pointSchema from '../schemas/components/point.schema.json';
import lineSchema from '../schemas/components/line.schema.json';
import circleSchema from '../schemas/components/circle.schema.json';
import squareSchema from '../schemas/components/square.schema.json';
import binSchema from '../schemas/components/bin.schema.json';
import dotpoolSchema from '../schemas/components/dotpool.schema.json';

export interface ValidationError {
  path: string;
  message: string;
  keyword?: string;
  params?: Record<string, any>;
}

export interface ValidationResult {
  valid: boolean;
  errors?: ValidationError[];
}

/**
 * Schema validator for BlitzDiagram configurations
 * Uses JSON Schema to validate diagram structure before instantiation
 */
export class SchemaValidator {
  private ajv: Ajv;
  private validateDiagram: ValidateFunction;

  constructor() {
    // Initialize AJV with strict mode
    this.ajv = new Ajv({
      allErrors: true,
      verbose: true,
      strict: true,
      strictSchema: true,
      allowUnionTypes: true
    });

    // Register all component schemas
    this.ajv.addSchema(tenframeSchema);
    this.ajv.addSchema(sliderSchema);
    this.ajv.addSchema(gridlayoutSchema);
    this.ajv.addSchema(textSchema);
    this.ajv.addSchema(pointSchema);
    this.ajv.addSchema(lineSchema);
    this.ajv.addSchema(circleSchema);
    this.ajv.addSchema(squareSchema);
    this.ajv.addSchema(binSchema);
    this.ajv.addSchema(dotpoolSchema);

    // Compile main diagram schema
    this.validateDiagram = this.ajv.compile(diagramSchema);
  }

  /**
   * Validate a diagram configuration
   * @param config - The diagram configuration to validate
   * @returns Validation result with errors if invalid
   */
  validate(config: unknown): ValidationResult {
    const valid = this.validateDiagram(config);
    
    if (!valid && this.validateDiagram.errors) {
      const errors = this.formatErrors(this.validateDiagram.errors);
      return { valid: false, errors };
    }

    return { valid: true };
  }

  /**
   * Validate and throw if invalid
   * @param config - The diagram configuration to validate
   * @throws {ValidationException} if validation fails
   */
  validateOrThrow(config: unknown): asserts config is DiagramConfig {
    const result = this.validate(config);
    
    if (!result.valid) {
      const errorMessages = result.errors!
        .map(e => `${e.path}: ${e.message}`)
        .join('\n');
      
      throw new ValidationException(
        `Diagram validation failed:\n${errorMessages}`,
        result.errors!
      );
    }
  }

  /**
   * Format AJV errors into a more readable structure
   */
  private formatErrors(errors: ErrorObject[]): ValidationError[] {
    return errors.map(error => ({
      path: error.instancePath || '/',
      message: error.message || 'Validation failed',
      keyword: error.keyword,
      params: error.params
    }));
  }

  /**
   * Get human-readable error message
   */
  getErrorMessage(result: ValidationResult): string {
    if (result.valid) {
      return 'Validation passed';
    }

    return result.errors!
      .map(e => `  • ${e.path || 'root'}: ${e.message}`)
      .join('\n');
  }
}

/**
 * Custom exception for validation errors
 */
export class ValidationException extends Error {
  constructor(
    message: string,
    public readonly errors: ValidationError[]
  ) {
    super(message);
    this.name = 'ValidationException';
    Object.setPrototypeOf(this, ValidationException.prototype);
  }
}

// Export singleton instance
export const schemaValidator: SchemaValidator = new SchemaValidator();
